import { Alert, Avatar, Button, Result, Skeleton, Tabs, message } from "antd";
import { useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageHeader from "components/PageHeader";
import { ERROR_CODE, FINAL_STATUSES, SOURCE_LABEL, STATUS_LABEL } from "features/ConsultRequest/core/constants";
import { formatDateTime } from "features/ConsultRequest/core/utils";
import { useConsultRequestDetail } from "features/ConsultRequest/data/hooks";
import { mapBudget, mapDealer, mapSnapshotPrice, mapTimeline } from "features/ConsultRequest/data/mappers";
import ActionButtons from "features/ConsultRequest/presentation/components/ActionButtons";
import AnswerList from "features/ConsultRequest/presentation/components/AnswerList";
import AssignModal from "features/ConsultRequest/presentation/components/AssignModal";
import ConsultErrorState from "features/ConsultRequest/presentation/components/ConsultErrorState";
import FilesTab from "features/ConsultRequest/presentation/components/FilesTab";
import HistoryTab from "features/ConsultRequest/presentation/components/HistoryTab";
import NotesTab from "features/ConsultRequest/presentation/components/NotesTab";
import PackageSnapshotDrawer from "features/ConsultRequest/presentation/components/PackageSnapshotDrawer";
import ProgressSteps from "features/ConsultRequest/presentation/components/ProgressSteps";
import ReadonlyBanner from "features/ConsultRequest/presentation/components/ReadonlyBanner";
import StatusChangeModal from "features/ConsultRequest/presentation/components/StatusChangeModal";
import "./index.scss";

const Info = ({ label, children }) => (
  <div className="consult-info-row">
    <span>{label}</span>
    <span>{children}</span>
  </div>
);

export default function ConsultRequestDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const history = useHistory();
  const query = useConsultRequestDetail(id);
  const detail = query.data;
  const [assignOpen, setAssignOpen] = useState(false);
  const [toStatus, setToStatus] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [historyToken, setHistoryToken] = useState(0);
  const bump = () => setHistoryToken((n) => n + 1);

  const backToList = <Button onClick={() => history.push("/consult-request")}>Quay lại danh sách</Button>;
  if (query.loading && !detail)
    return (
      <div style={{ padding: 24 }}>
        <Skeleton active paragraph={{ rows: 10 }} />
      </div>
    );
  if (query.error?.code === ERROR_CODE.NOT_FOUND)
    return <Result status="404" title="Không tìm thấy phiếu" extra={backToList} />;
  if (query.error && !detail)
    return <ConsultErrorState error={query.error} onRetry={query.refetch} extra={backToList} />;
  if (!detail) return null;

  const dealer = mapDealer(detail.dealer);
  const permissions = detail.permissions || {};
  const final = FINAL_STATUSES.includes(detail.status);
  const request = { id: detail.id, version: detail.version };
  const reload = () => query.refetch();
  // Sau MỖI thao tác ghi thành công: tải lại chi tiết (version mới) và nhật ký, kể cả khi tab Nhật ký chưa mở.
  const afterWrite = (next) => {
    if (next) query.setData(next);
    reload();
    bump();
  };

  return (
    <div className="consult-detail-page">
      <PageHeader
        pageTitle={t("v2.consultRequest.detailTitle")}
        routes={[
          { name: t("v2.consultRequest.breadcrumbRoot"), path: "/seller" },
          { name: t("v2.consultRequest.menu"), path: "/consult-request" },
          { name: detail.code },
        ]}
      />
      <div className="consult-header-card">
        <div style={{ display: "flex", alignItems: "center" }}>
          <Avatar size={32} style={{ background: "#F6F6F6", color: "#5b667f", marginRight: 8 }}>
            {dealer.companyName?.[0]}
          </Avatar>
          <b>{dealer.companyName}</b>
          <span className={`consult-badge ${final ? "consult-badge-final" : ""}`}>{STATUS_LABEL[detail.status]}</span>
        </div>
        <ActionButtons
          permissions={permissions}
          onAssign={() => setAssignOpen(true)}
          onReassign={() => setAssignOpen(true)}
          onSelect={setToStatus}
        />
      </div>
      <ReadonlyBanner detail={detail} />
      {detail.duplicateOf && (
        <Alert
          type="warning"
          message={
            <span>
              Phiếu có thể trùng với{" "}
              <Link to={`/consult-request/detail/${detail.duplicateOf.id}`}>{detail.duplicateOf.code}</Link>
            </span>
          }
        />
      )}
      <div className="consult-card">
        <ProgressSteps status={detail.status} milestones={detail.progressMilestones} />
      </div>
      <div className="consult-card" style={{ display: "flex", gap: 20 }}>
        <div style={{ flex: 1 }}>
          <b>Thông tin khách hàng</b>
          <Info label="Tên công ty">{dealer.companyName}</Info>
          {dealer.taxCode && <Info label="MST">{dealer.taxCode}</Info>}
          <Info label="SĐT">{detail.dealer?.phone}</Info>
          <Info label="Địa chỉ">{detail.dealer?.address}</Info>
          <Info label="Kỹ thuật viên phụ trách">
            {detail.assignee?.name ? `${detail.assignee.name} · Phân công: ${formatDateTime(detail.assignedAt)}` : "--"}
          </Info>
          <AnswerList answers={detail.answers} />
        </div>
        <div style={{ flex: 1 }}>
          <b>Thông tin chung</b>
          <Info label="Mã phiếu">{detail.code}</Info>
          <Info label="Loại dự án">{detail.projectTypeName}</Info>
          <Info label="Tên công trình">{detail.siteName}</Info>
          <Info label="Địa điểm">{detail.addressText}</Info>
          <Info label="Thời gian triển khai">{mapTimeline(detail)}</Info>
          <Info label="Ngân sách">{mapBudget(detail)}</Info>
          {detail.mainQuantity != null && (
            <Info label="Số lượng">{`${detail.mainQuantity} ${detail.mainQuantityUnit || ""}`}</Info>
          )}
          <Info label="Sản phẩm quan tâm">{(detail.interestedProducts || []).map((p) => p.name).join(", ")}</Info>
          <Info label="Mô tả nhu cầu">{detail.description}</Info>
          <Info label="Ghi chú thêm của khách">{detail.note}</Info>
          <Info label="Nguồn">{SOURCE_LABEL[detail.source]}</Info>
          <Info label="Thời gian gửi">{formatDateTime(detail.createdAt)}</Info>
        </div>
      </div>
      <div className="consult-card">
        <b>Gói đề xuất khách đã xem</b>
        {detail.viewedPackageSnapshot ? (
          <div>
            {`${detail.viewedPackageSnapshot.name} · ${detail.viewedPackageSnapshot.products?.length || 0} sản phẩm · ${mapSnapshotPrice(detail.viewedPackageSnapshot)} `}
            <Button type="link" onClick={() => setDrawerOpen(true)}>
              Xem chi tiết ›
            </Button>
          </div>
        ) : (
          <div>Khách không xem gói đề xuất nào</div>
        )}
      </div>
      <div className="consult-card">
        <Tabs defaultActiveKey="notes">
          <Tabs.TabPane key="notes" tab="Ghi chú">
            <NotesTab
              requestId={detail.id}
              canAddNote={permissions.canAddNote}
              onNoteAdded={() => afterWrite()}
              onStale={reload}
            />
          </Tabs.TabPane>
          <Tabs.TabPane key="history" tab="Tiến độ xử lý phiếu" forceRender>
            <HistoryTab requestId={detail.id} refreshToken={historyToken} />
          </Tabs.TabPane>
          <Tabs.TabPane key="files" tab={`File đính kèm & tài liệu khảo sát (${detail.fileCount ?? 0})`}>
            <FilesTab requestId={detail.id} permissions={permissions} onChanged={() => afterWrite()} onStale={reload} />
          </Tabs.TabPane>
        </Tabs>
      </div>
      <AssignModal
        visible={assignOpen}
        request={request}
        onClose={() => setAssignOpen(false)}
        onAssigned={afterWrite}
        onStale={reload}
      />
      <StatusChangeModal
        visible={Boolean(toStatus)}
        toStatus={toStatus}
        request={request}
        onClose={() => setToStatus(null)}
        onChanged={(next) => {
          afterWrite(next);
          message.success("Đã cập nhật trạng thái");
        }}
        onStale={reload}
      />
      <PackageSnapshotDrawer
        visible={drawerOpen}
        snapshot={detail.viewedPackageSnapshot}
        onClose={() => setDrawerOpen(false)}
      />
    </div>
  );
}
